import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusIcon, ArrowUpTrayIcon, FilmIcon } from "@heroicons/react/24/outline";
import { useAuth } from "./context/AuthContext";
import BackArrow from "./components/GeneratePage/BackArrow";
import api from "./services/api";

interface HookVideo {
  id: string;
  video_url: string;
  thumbnail_url?: string;
  created_at: string;
  status: string;
  video_type?: string; // "background" or "hook"
}

interface VideoGenerationRequest {
  image_url?: string;
  image_path?: string;
  custom_prompt?: string;
  video_type?: string;
}

const ManageHookVideosPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State for image generation
  const [prompt, setPrompt] = useState<string>("");
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [localImagePath, setLocalImagePath] = useState<string | null>(null);

  // State for video generation
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState<string>("");

  // State for video list
  const [hookVideos, setHookVideos] = useState<HookVideo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // State for drag and drop upload
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadedMedia, setUploadedMedia] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // State for progress and errors
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Generate image based on prompt
  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    // Validate prompt length
    if (prompt.length > 1000) {
      setError("Prompt is too long. Please keep it under 1000 characters.");
      return;
    }

    setIsGeneratingImage(true);
    setProgress(0);
    setError("");
    setSuccess("");

    // Set up a timeout to handle long-running requests
    const timeoutId = setTimeout(() => {
      if (isGeneratingImage) {
        setProgress((prevProgress) => Math.min(prevProgress + 10, 95));
      }
    }, 5000);

    try {
      setProgress(10);

      const response = await api.post("/api/characters/generate-images", {
        prompt: prompt,
      });

      clearTimeout(timeoutId);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to generate image");
      }

      setProgress(50);

      // Validate response format
      if (!response.data.images || !Array.isArray(response.data.images) || response.data.images.length === 0) {
        throw new Error("No images received from the server");
      }

      // Get first image from response
      const base64Image = response.data.images[0];
      const imageUrl = `data:image/png;base64,${base64Image}`;
      setGeneratedImage(imageUrl);

      // Store local path if available
      if (response.data.local_paths && response.data.local_paths.length > 0) {
        setLocalImagePath(response.data.local_paths[0]);
      }

      setProgress(100);
      setSuccess("Image generated successfully!");
    } catch (err: any) {
      console.error("Error generating image:", err);

      // Clear timeout to prevent further progress updates
      clearTimeout(timeoutId);

      // Provide more detailed error information
      if (err.response) {
        // The server responded with an error status
        const errDetail = err.response.data?.detail || err.response.data?.error || "";
        setError(`Server error (${err.response.status}): ${errDetail || err.message}`);
      } else if (err.request) {
        // The request was made but no response was received
        setError("No response from server. Please check your connection and try again.");
      } else {
        // Something happened in setting up the request
        setError(err.message || "An unknown error occurred");
      }

      // Reset progress to 0 on error
      setProgress(0);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Generate background video using image
  const handleGenerateVideo = async () => {
    if (!generatedImage) {
      setError("Please generate an image first");
      return;
    }

    // Check if user has enough credits
    if (user?.videos_left <= 0) {
      setError("You don't have enough credits to generate a video. Please purchase more credits.");
      return;
    }

    setIsGeneratingVideo(true);
    setProgress(0);
    setError("");
    setSuccess("");

    // Set up a timeout to gradually update progress for long-running requests
    const progressInterval = setInterval(() => {
      if (isGeneratingVideo) {
        setProgress((prevProgress) => {
          // Gradually increase progress, but never reach 100% (which indicates completion)
          if (prevProgress < 90) {
            return prevProgress + (90 - prevProgress) * 0.1;
          }
          return prevProgress;
        });
      }
    }, 2000);

    try {
      setProgress(10);

      // Prepare request data as JSON
      const requestData: VideoGenerationRequest = {
        video_type: "background" // Always set as background type from this page
      };

      // Only include image_path if we have a valid path
      if (localImagePath && localImagePath.trim() !== "") {
        requestData.image_path = localImagePath;
      }

      // Always include image_url as long as we have a generated image
      // Make sure to handle base64 images properly - the API expects a proper URL or base64 data
      if (generatedImage.startsWith('data:image')) {
        // The backend expects base64 images to be in proper format
        requestData.image_url = generatedImage;
      } else {
        // If it's a URL, ensure it's properly formatted
        requestData.image_url = generatedImage;
      }

      // Include custom prompt if provided
      if (customPrompt.trim() !== "") {
        requestData.custom_prompt = customPrompt.trim();
      }

      console.log(
        "Sending request with image_url:",
        generatedImage.substring(0, 50) + "..."
      );
      if (localImagePath)
        console.log("Sending request with image_path:", localImagePath);
      if (customPrompt.trim())
        console.log("Sending request with custom prompt:", customPrompt.trim());

      const response = await api.post("/api/hook/generate-video", requestData);

      // Clear the progress interval
      clearInterval(progressInterval);

      // Check for valid response
      if (!response.data) {
        throw new Error("No data received from server");
      }

      // Check for API error
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // Validate that we got a video URL back
      if (!response.data.video_url) {
        throw new Error("No video URL returned from server");
      }

      setProgress(100);
      setGeneratedVideo(response.data.video_url);
      setSuccess("Background video generated successfully!");

      // Refresh videos list
      fetchHookVideos();
    } catch (err: any) {
      console.error("Error generating video:", err);

      // Clear the progress interval
      clearInterval(progressInterval);

      // Provide more detailed error information
      if (err.response) {
        // The server responded with an error status
        console.error("Server response error:", {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });

        // Check for credit-related errors
        if (err.response.status === 402) {
          setError("You don't have enough credits to generate a video. Please purchase more credits.");
        }
        // Display a more helpful error message with the actual error from the backend if available
        else if (err.response.data && err.response.data.detail) {
          setError(`Server error: ${err.response.data.detail}`);
        } else {
          setError(`Server error (${err.response.status}): ${err.message}`);
        }
      } else if (err.request) {
        // The request was made but no response was received
        console.error("No response received:", err.request);
        setError("No response from server. Please check your connection and try again.");
      } else {
        // Something happened in setting up the request
        console.error("Request setup error:", err.message);
        setError(err.message || "Failed to generate video");
      }

      // Reset progress
      setProgress(0);
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // Save hook video background
  const handleSaveVideo = async () => {
    if (!generatedVideo) {
      setError("Please generate a video first");
      return;
    }

    try {
      setIsLoading(true);

      // Prepare request data as JSON
      const requestData = {
        video_url: generatedVideo,
        video_type: "background" // Always save as background type from this page
      };

      const response = await api.post("/api/hook/save", requestData);

      if (!response.data || response.data.error) {
        throw new Error(response.data.error || "Failed to save video");
      }

      setSuccess("Background video saved successfully!");

      // Reset form
      setPrompt("");
      setCustomPrompt("");
      setGeneratedImage(null);
      setGeneratedVideo(null);

      // Refresh videos list
      fetchHookVideos();
    } catch (err) {
      console.error("Error saving video:", err);
      setError(err instanceof Error ? err.message : "Failed to save video");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch hook videos - request more videos per page (16 instead of default 4)
  const fetchHookVideos = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/api/hook/list-hook-videos?page_size=16&video_type=background");

      if (response.data && response.data.items) {
        setHookVideos(response.data.items);
      }
    } catch (err) {
      console.error("Error fetching background videos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Download a hook video
  const handleDownload = async (hookId: string) => {
    try {
      const response = await api.get(`/api/hook/download/${hookId}`);

      if (response.data && response.data.download_url) {
        window.open(response.data.download_url, "_blank");
      }
    } catch (err) {
      console.error("Error downloading video:", err);
      setError("Failed to download video");
    }
  };

  // Delete a hook video
  const handleDelete = async (hookId: string) => {
    try {
      await api.delete(`/api/hook/delete/${hookId}`);
      setSuccess("Video deleted successfully!");
      fetchHookVideos();
    } catch (err) {
      console.error("Error deleting video:", err);
      setError("Failed to delete video");
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleVideoFile(file);
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleVideoFile(file);
    }
  }, []);

  const handleVideoFile = (file: File) => {
    // Check if file is a video or image
    if (!file.type.startsWith('video/') && !file.type.startsWith('image/')) {
      setError("Please upload a valid video or image file");
      return;
    }

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      setError("File is too large. Maximum size is 50MB");
      return;
    }

    setUploadedMedia(file);
    setError("");
  };

  // Upload the media and save it as a hook background
  const handleUploadVideo = async () => {
    if (!uploadedMedia) {
      setError("Please select a file to upload");
      return;
    }

    // Check if user has enough credits
    if (user?.videos_left <= 0) {
      setError("You don't have enough credits to upload. Please purchase more credits.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError("");
    setSuccess("");

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", uploadedMedia);

      // Use our new dedicated endpoint for hook background media
      const uploadResponse = await api.post("/api/hook/upload-background-media", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 100)
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (!uploadResponse.data || !uploadResponse.data.file_url) {
        throw new Error("Upload failed - no file URL returned");
      }

      setUploadProgress(100);

      // Set success message based on file type
      if (uploadedMedia.type.startsWith('video/')) {
        setSuccess("Background video uploaded and saved successfully!");
      } else {
        setSuccess("Background image uploaded and saved successfully!");
      }
      setUploadedMedia(null);

      // Refresh the videos list
      fetchHookVideos();
    } catch (err: any) {
      console.error("Error uploading media:", err);

      if (err.response) {
        if (err.response.status === 402) {
          setError("You don't have enough credits to upload. Please purchase more credits.");
        } else if (err.response.status === 422) {
          console.error("Validation error details:", err.response.data);
          setError(`Validation error: ${err.response.data?.detail || "Missing required fields"}. Please check console for details.`);
        } else if (err.response.status === 400) {
          setError(`${err.response.data?.detail || "Invalid file type or size"}`);
        } else {
          setError(`Server error (${err.response.status}): ${err.response.data?.detail || err.message}`);
        }
      } else if (err.request) {
        setError("No response from server. Please check your connection and try again.");
      } else {
        setError(err.message || "Failed to upload media");
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Load videos on component mount
  useEffect(() => {
    fetchHookVideos();
  }, []);

  return (
    <div className="container mx-auto p-4 pb-24">
      {/* Navigation Header */}
      <div className="border-gray-200 dark:border-gray-800 py-3 mb-6 bg-white dark:bg-gray-900 transition-colors duration-200 px-4 rounded-xl border border-b-3">
        <div className="flex items-center justify-between">
          {/* Back button */}
          <button
            onClick={() => navigate("/hook-videos")}
            className="flex items-center text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 hover:cursor-pointer"
          >
            <BackArrow />
            Back
          </button>

          <h1 className="text-xl font-bold">AI Avatar Backgrounds</h1>

          {/* Credits indicator */}
          <div className="flex items-center justify-center">
            <Link
              to="/pricing"
              className="border border-gray-300 px-3 py-1 flex flex-row items-center rounded-md hover:cursor-pointer relative pb-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="h-4 w-4 fill-yellow-500 mr-1"
              >
                <path d="M512 80c0 18-14.3 34.6-38.4 48c-29.1 16.1-72.5 27.5-122.3 30.9c-3.7-1.8-7.4-3.5-11.3-5C300.6 137.4 248.2 128 192 128c-8.3 0-16.4 .2-24.5 .6l-1.1-.6C142.3 114.6 128 98 128 80c0-44.2 86-80 192-80S512 35.8 512 80zM160.7 161.1c10.2-.7 20.7-1.1 31.3-1.1c62.2 0 117.4 12.3 152.5 31.4C369.3 204.9 384 221.7 384 240c0 4-.7 7.9-2.1 11.7c-4.6 13.2-17 25.3-35 35.5c0 0 0 0 0 0c-.1 .1-.3 .1-.4 .2c0 0 0 0 0 0s0 0 0 0c-.3 .2-.6 .3-.9 .5c-35 19.4-90.8 32-153.6 32c-59.6 0-112.9-11.3-148.2-29.1c-1.9-.9-3.7-1.9-5.5-2.9C14.3 274.6 0 258 0 240c0-34.8 53.4-64.5 128-75.4c10.5-1.5 21.4-2.7 32.7-3.5zM416 240c0-21.9-10.6-39.9-24.1-53.4c28.3-4.4 54.2-11.4 76.2-20.5c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 19.3-16.5 37.1-43.8 50.9c-14.6 7.4-32.4 13.7-52.4 18.5c.1-1.8 .2-3.5 .2-5.3zm-32 96c0 18-14.3 34.6-38.4 48c-1.8 1-3.6 1.9-5.5 2.9C304.9 404.7 251.6 416 192 416c-62.8 0-118.6-12.6-153.6-32C14.3 370.6 0 354 0 336l0-35.4c12.5 10.3 27.6 18.7 43.9 25.5C83.4 342.6 135.8 352 192 352s108.6-9.4 148.1-25.9c7.8-3.2 15.3-6.9 22.4-10.9c6.1-3.4 11.8-7.2 17.2-11.2c1.5-1.1 2.9-2.3 4.3-3.4l0 3.4 0 5.7 0 26.3zm32 0l0-32 0-25.9c19-4.2 36.5-9.5 52.1-16c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 10.5-5 21-14.9 30.9c-16.3 16.3-45 29.7-81.3 38.4c.1-1.7 .2-3.5 .2-5.3zM192 448c56.2 0 108.6-9.4 148.1-25.9c16.3-6.8 31.5-15.2 43.9-25.5l0 35.4c0 44.2-86 80-192 80S0 476.2 0 432l0-35.4c12.5 10.3 27.6 18.7 43.9 25.5C83.4 438.6 135.8 448 192 448z" />
              </svg>
              <span>{user?.videos_left || 0} credits left</span>
              <span className="ml-2 bg-gray-200 rounded-md bg-gradient-to-b from-purple-700 to-purple-400 border-2 border-b-3 border-purple-800 fill-white hover:cursor-pointer p-0.5">
                <PlusIcon className="h-4 w-4" />
              </span>
              {/* Credit progress bar */}
              <div className="absolute bottom-0 left-0 w-full h-1 overflow-hidden rounded-b-md">
                <div
                  className="h-full bg-blue-500 shadow-[0_0_5px_2px_rgba(59,130,246,0.5)] transition-all duration-300 ease-in-out"
                  style={{ width: `${((user?.videos_left || 0) / 20) * 100}%` }}
                ></div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Status messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          {success}
        </div>
      )}

      {/* Progress bar */}
      {(isGeneratingImage || isGeneratingVideo) && (
        <div className="w-full bg-gray-200 rounded-full h-4 mb-6">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left side - Form controls */}
        <div className="w-full md:w-1/2 space-y-6">
          {/* Image generation section */}
          <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">
              Generate Background Image
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Image Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                placeholder="Describe the background image you want to generate..."
                rows={4}
                disabled={isGeneratingImage}
              />
            </div>

            <button
              onClick={handleGenerateImage}
              disabled={isGeneratingImage || !prompt.trim()}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            >
              {isGeneratingImage ? "Generating..." : "Generate Image"}
            </button>
          </div>

          {/* Video generation section */}
          <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">
              Generate Background Video
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Action Instructions
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-white"
                placeholder="E.g., flipping hair, smiling, looking thoughtful, nodding, shaking head left and right"
                rows={2}
                disabled={isGeneratingVideo}
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Enter an action that you want the person to perform in the video (without using "person" prefix). For best results, use clear action verbs like <span className="font-medium text-purple-600 dark:text-purple-400">"shaking head left and right"</span>, <span className="font-medium text-purple-600 dark:text-purple-400">"flipping hair"</span>, or <span className="font-medium text-purple-600 dark:text-purple-400">"nodding"</span>. Keep it short and direct.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleGenerateVideo}
                disabled={isGeneratingVideo || !generatedImage}
                className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                {isGeneratingVideo ? "Generating..." : "Generate Video"}
              </button>

              <button
                onClick={handleSaveVideo}
                disabled={!generatedVideo}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                Save Video
              </button>
            </div>
          </div>
        </div>

        {/* Right side - Preview */}
        <div className="w-full md:w-1/2">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow h-full">
            <h2 className="text-lg font-semibold mb-4">Preview</h2>

            {generatedImage ? (
              <div className="mb-6">
                <h3 className="text-md font-medium mb-2">Generated Image</h3>
                <div className="border-4 rounded-lg border-transparent hover:border-gray-300 transition-all">
                  <img
                    src={generatedImage}
                    alt="Generated background"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
              </div>
            ) : (
              <div className="mb-6 p-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <p className="text-gray-500 dark:text-gray-400">
                  Generate an image to see preview
                </p>
              </div>
            )}

            {generatedVideo ? (
              <div>
                <h3 className="text-md font-medium mb-2">Generated Video</h3>
                <div className="relative aspect-[9/16] overflow-hidden rounded-lg">
                  <video
                    src={generatedVideo}
                    controls
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      console.error("Video preview error:", e);
                      setError("Error playing the generated video. The video may be unavailable or in an unsupported format.");
                    }}
                  />
                </div>
              </div>
            ) : (
              generatedImage && (
                <div className="p-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    Generate a background video to see preview
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Upload Background Media Section */}
      <div className="mt-8">
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow">
          <h2 className="text-2xl font-bold mb-4">Upload Background Media</h2>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Drag & Drop Upload Area */}
            <div
              className={`flex-1 border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-colors
                ${isDragging
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                  : "border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600"}
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('videoUploadInput')?.click()}
            >
              <input
                type="file"
                id="videoUploadInput"
                className="hidden"
                accept="video/*,image/*"
                onChange={handleFileInputChange}
              />

              <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />

              {uploadedMedia ? (
                <div className="text-center">
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">{uploadedMedia.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {(uploadedMedia.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Drag & Drop your video or image here
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Or click to browse (max 50MB)
                  </p>
                </div>
              )}
            </div>

            {/* Upload Media Preview and Controls */}
            <div className="flex-1 flex flex-col">
              {uploadedMedia && (
                <>
                  <div className="aspect-[9/16] bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden mb-4">
                    {uploadedMedia && uploadedMedia.type.startsWith('video/') ? (
                      <video
                        className="w-full h-full object-contain"
                        controls
                        src={URL.createObjectURL(uploadedMedia)}
                      />
                    ) : (
                      <img
                        className="w-full h-full object-contain"
                        src={uploadedMedia ? URL.createObjectURL(uploadedMedia) : ""}
                        alt="Uploaded preview"
                      />
                    )}
                  </div>

                  {isUploading ? (
                    <div className="mt-4">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-2">
                        <div
                          className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-center text-gray-600 dark:text-gray-400">
                        Uploading... {uploadProgress}%
                      </p>
                    </div>
                  ) : (
                    <div className="mt-4 flex space-x-3">
                      <button
                        onClick={handleUploadVideo}
                        className="flex-1 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                        disabled={!uploadedMedia}
                      >
                        Upload as Background {uploadedMedia?.type.startsWith('video/') ? 'Video' : 'Image'}
                      </button>
                      <button
                        onClick={() => setUploadedMedia(null)}
                        className="bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}

              {!uploadedMedia && (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                  <FilmIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400 text-center">
                    Upload an image or video to use as a hook background. Both will be available in the hook videos page to add text overlays.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* All Hook Videos Management Section */}
      <div className="mt-8 mb-12">
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold">Manage All Background Media</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                These images and videos will appear in the Hook Videos page for AI avatars
              </p>
            </div>

            <div>
              <button
                onClick={() => fetchHookVideos()}
                className="flex items-center gap-2 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded-md px-4 py-2 text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">
                Loading videos...
              </p>
            </div>
          ) : hookVideos.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-gray-400 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <p className="text-gray-500 dark:text-gray-400 font-medium">
                No background media available
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                Generate or upload an image or video using the forms above
              </p>
            </div>
          ) : (
            <>
              {/* Table View */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 rounded-l-lg">Preview</th>
                      <th className="px-6 py-3">ID</th>
                      <th className="px-6 py-3">Created</th>
                      <th className="px-6 py-3">Source</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3 rounded-r-lg">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hookVideos.map((video) => (
                      <tr
                        key={video.id}
                        className="bg-white dark:bg-gray-900 border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <td className="px-6 py-4">
                          <div className="aspect-[9/16] h-16 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                            {video.status === "completed" ? (
                              <>
                                {video.thumbnail_url ? (
                                  <img
                                    src={video.thumbnail_url}
                                    alt="Video thumbnail"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <video
                                    src={video.video_url}
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs max-w-[120px] truncate">
                          {video.id}
                        </td>
                        <td className="px-6 py-4">
                          {new Date(video.created_at).toLocaleDateString()}
                          <span className="text-gray-500 dark:text-gray-400 ml-2 text-xs">
                            {new Date(video.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            (video.video_url?.includes('temp/uploaded') || video.video_url?.includes('upload-file'))
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                              : "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                          }`}>
                            {(video.video_url?.includes('temp/uploaded') || video.video_url?.includes('upload-file'))
                              ? "Uploaded"
                              : "Generated"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            video.status === "completed"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                          }`}>
                            {video.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            {video.status === "completed" && (
                              <>
                                <button
                                  onClick={() => window.open(video.video_url, "_blank")}
                                  className="bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded px-3 py-1 text-xs font-medium"
                                >
                                  Preview
                                </button>
                                <button
                                  onClick={() => handleDownload(video.id)}
                                  className="bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 rounded px-3 py-1 text-xs font-medium"
                                >
                                  Download
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(video.id)}
                              className="bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 rounded px-3 py-1 text-xs font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageHookVideosPage;
